<template>
  <div class="year-month-selector">
    <div class="input-wrapper" @click="toggleDropdown">
      <v-icon class="input-icon">{{ prependInnerIcon }}</v-icon>
      <span class="input-text">{{ displayValue || label }}</span>
      <v-icon class="dropdown-icon">
        {{ isOpen ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
      </v-icon>
    </div>

    <!-- 드롭다운 모달 -->
    <div v-if="isOpen" class="dropdown-modal">
      <div class="dropdown-content">
        <!-- 연도 선택 -->
        <div class="year-section">
          <div class="section-title">연도 선택</div>
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            :disabled="isDisabled"
            variant="outlined"
            density="compact"
            hide-details
            :menu-props="{ zIndex: 10000 }"
            @update:model-value="onYearChange"
          />
        </div>

        <!-- 월 선택 -->
        <div class="month-section">
          <div class="section-title">월 선택</div>
          <div class="month-grid">
            <button
              v-for="month in monthOptions"
              :key="month.value"
              class="month-button"
              :class="{ active: selectedMonth === month.value, disabled: isDisabled }"
              :disabled="isDisabled"
              @click="selectMonth(month.value)"
            >
              {{ month.label }}
            </button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

interface Props {
  modelValue?: string;
  label?: string;
  prependInnerIcon?: string;
  variant?: string;
  density?: string;
  rules?: any[];
  hideDetails?: boolean;
}

interface Emits {
  (e: 'update:modelValue', value: string): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: '년월 선택',
  prependInnerIcon: 'mdi-calendar',
  variant: 'outlined',
  density: 'compact',
  hideDetails: false
});

const emit = defineEmits<Emits>();
const store = useStore();

// 반응형 데이터
const isOpen = ref(false);
const selectedYear = ref<number | null>(null);
const selectedMonth = ref<number | null>(null);
const isDisabled = ref(false);

// 년도 옵션 (2025년부터 현재 년도까지)
const currentYear = new Date().getFullYear();
const yearOptions = computed(() => {
  const years = [];
  for (let i = 2025; i <= currentYear; i++) {
    years.push(i);
  }
  return years;
});

// 한 달 전 연월 계산
const getLastMonthYearMonth = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const year = lastMonth.getFullYear();
  const month = lastMonth.getMonth() + 1;
  return normalizeYearMonth(year, month);
};

// 월 옵션
const monthOptions = [
  { value: 1, label: '1월' },
  { value: 2, label: '2월' },
  { value: 3, label: '3월' },
  { value: 4, label: '4월' },
  { value: 5, label: '5월' },
  { value: 6, label: '6월' },
  { value: 7, label: '7월' },
  { value: 8, label: '8월' },
  { value: 9, label: '9월' },
  { value: 10, label: '10월' },
  { value: 11, label: '11월' },
  { value: 12, label: '12월' }
];

// 표시값
const displayValue = computed(() => {
  if (props.modelValue && props.modelValue.length === 6) {
    const year = props.modelValue.substring(0, 4);
    const month = props.modelValue.substring(4, 6);
    return `${year}년 ${parseInt(month)}월`;
  }
  return '';
});

const storeLebYm = computed(() => store.state.lebYm);

const normalizeYearMonth = (year: number, month: number) => {
  return `${year}${month.toString().padStart(2, '0')}`;
};

const syncStoreWithValue = (value?: string) => {
  if (!value || value.length !== 6) return;
  if (store.state.lebYm !== value) {
    store.commit('setLebYm', value);
  }
};

// 드롭다운 토글
const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown();
  } else {
    openDropdown();
  }
};

// 드롭다운 열기
const openDropdown = () => {
  isOpen.value = true;
  // 현재 값이 있으면 파싱해서 선택
  if (props.modelValue && props.modelValue.length === 6) {
    selectedYear.value = parseInt(props.modelValue.substring(0, 4));
    selectedMonth.value = parseInt(props.modelValue.substring(4, 6));
  } else {
    // 기본값 설정 (한 달 전)
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    selectedYear.value = lastMonth.getFullYear();
    selectedMonth.value = lastMonth.getMonth() + 1;
  }
};


// 연도 선택
const onYearChange = (year: number | null) => {
  if (isDisabled.value || !year) return;
  
  selectedYear.value = year;
  // 연도 선택 시 즉시 업데이트
  if (selectedMonth.value) {
    const yearMonth = normalizeYearMonth(year, selectedMonth.value);
    emit('update:modelValue', yearMonth);
    syncStoreWithValue(yearMonth);
  }
  
  // 1초간 비활성화
  disableButtons();
};

// 월 선택
const selectMonth = (month: number) => {
  if (isDisabled.value) return;
  
  selectedMonth.value = month;
  // 월 선택 시 즉시 업데이트
  if (selectedYear.value) {
    const yearMonth = normalizeYearMonth(selectedYear.value, month);
    emit('update:modelValue', yearMonth);
    syncStoreWithValue(yearMonth);
    // 연도와 월이 모두 선택되어 있으면 드롭다운 닫기
    closeDropdown();
  }
  
  // 1초간 비활성화
  disableButtons();
};


// 버튼 비활성화 함수
const disableButtons = () => {
  isDisabled.value = true;
  setTimeout(() => {
    isDisabled.value = false;
  }, 1000);
};

// 드롭다운 닫기
const closeDropdown = () => {
  isOpen.value = false;
};

// 외부 클릭 감지
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  // v-select 메뉴는 body에 teleport되므로 별도로 체크
  if (!target.closest('.year-month-selector') && !target.closest('.v-overlay')) {
    closeDropdown();
  }
};

const initializeFromStore = () => {
  const storedValue = storeLebYm.value;
  if (storedValue && storedValue.length === 6) {
    if (props.modelValue !== storedValue) {
      emit('update:modelValue', storedValue);
    }
    return;
  }
  if (props.modelValue && props.modelValue.length === 6) {
    syncStoreWithValue(props.modelValue);
    return;
  }
  // 기본값: 한 달 전 연월
  const lastMonthYm = getLastMonthYearMonth();
  emit('update:modelValue', lastMonthYm);
  syncStoreWithValue(lastMonthYm);
};

watch(
  () => props.modelValue,
  (value) => {
    syncStoreWithValue(value);
  }
);

watch(
  storeLebYm,
  (value) => {
    if (value && value.length === 6 && value !== props.modelValue) {
      emit('update:modelValue', value);
    }
  }
);

// 이벤트 리스너 등록/해제
onMounted(() => {
  initializeFromStore();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
.year-month-selector {
  position: relative;
}

.dropdown-modal {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 9999;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
  min-width: 300px;
}

.dropdown-content {
  padding: 16px;
  max-height: 450px;
  overflow-y: auto;
}

.section-title {
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
  font-size: 14px;
}

.year-section {
  margin-bottom: 20px;
}

.month-section {
  margin-bottom: 20px;
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.month-button {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;

  &:hover {
    background-color: #f5f5f5;
    border-color: #1976d2;
  }

  &.active {
    background-color: #1976d2;
    color: white;
    border-color: #1976d2;
  }

  &.disabled,
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
}


.cursor-pointer {
  cursor: pointer;
}

.input-wrapper {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;
  min-height: 40px;
  
  &:hover {
    border-color: #1976d2;
  }
}

.input-icon {
  margin-right: 8px;
  color: #666;
}

.input-text {
  flex: 1;
  color: #333;
  font-size: 14px;
}

.dropdown-icon {
  margin-left: 8px;
  color: #666;
}

</style>
